import React, { useEffect, useMemo, useRef, useState } from "react";
import { Comment } from "@highnote/server/src/core/entities";
import { StatefulComment, useSpaceCommentsContext } from "./useSpaceComments";
import { useTrack } from "App/components/useEntities/useTrack";
import { useAdsContext } from "./Ads/AdsContext";
import { mergeAdsIntoCards } from "./Ads/mergeAds";
import { CarouselAdCard } from "./Ads/CarouselAdCard";
import { Ad } from "./Ads/AdModel";

export type Card = {
  id: Id;
  createdAt: number;
  timestamp: number;
  comment?: Comment;
  renderComponent?: () => JSX.Element;
};

const getCardsFromComments = (comments: StatefulComment[] = []) => {
  const rootComments = comments.filter(
    (c) => !c.parentComment && typeof c.timestamp === "number",
  );

  return rootComments.map((comment) => ({
    id: comment.id,
    hidden: false,
    draft: false,
    createdAt: comment.createdAt,
    timestamp: comment.timestamp,
    comment,
  }));
};

const sortCards = (card1: Card, card2: Card) => {
  if (card1.timestamp < card2.timestamp) return -1;
  if (card1.timestamp > card2.timestamp) return 1;
  if (card1.timestamp === card2.timestamp) {
    if (card1.createdAt < card2.createdAt) return -1;
    if (card1.createdAt >= card2.createdAt) return 1;
  }
};

const DEFAULT_CARD: Card = {
  id: "insert-default-card",
  createdAt: 0, // arbitrary number, not used for sorting
  timestamp: 0, // arbitrary number, not used for sorting
};

export type AddCard = {
  insertAt: "first" | "last";
  component: () => JSX.Element;
};

// Factory that produces a named functional component for a given ad so
// lint's `react/display-name` is satisfied without inlining anonymous
// functions at the call-site.
const buildAdRenderer = (ad: Ad) => {
  const AdCardRenderer = () => <CarouselAdCard ad={ad} />;
  AdCardRenderer.displayName = `CarouselAdCardRenderer(${ad.id})`;
  return AdCardRenderer;
};

export const useCommentCards = (addCard?: AddCard) => {
  const { filteredComments } = useSpaceCommentsContext();
  const { currentTrackVersion } = useTrack();
  const { carouselAds, config } = useAdsContext();
  const cardsRef = useRef<Card[]>(getCardsFromComments(filteredComments));
  const getOrderedCards = () =>
    cardsRef.current.length > 0 ? cardsRef.current.sort(sortCards) : [];
  const orderedCardsRef = useRef(getOrderedCards());
  const [orderedCards, _setOrderedCards] = useState<Card[]>(
    orderedCardsRef.current,
  );

  const setOrderedCards = () => {
    orderedCardsRef.current = [...getOrderedCards()];
    _setOrderedCards(orderedCardsRef.current);
  };

  useEffect(() => {
    const allCards = getCardsFromComments(filteredComments);
    cardsRef.current = allCards.filter((c) =>
      c.comment.trackVersionIds?.includes(currentTrackVersion?.id),
    );
    setOrderedCards();
    // `carouselAds` is included so ads folding in asynchronously
    // re-triggers merge-and-sort; real comments still render immediately
    // on first pass.
  }, [filteredComments, currentTrackVersion?.id, carouselAds]);

  return useMemo(() => {
    // Merge ads AFTER sort so that priority-ordered ads are interleaved into
    // the already-timestamp-sorted comment feed. The server has already
    // sorted ads by priority; we do not re-sort ads here.
    // Respects the server-side kill switch: when `carouselEnabled` is
    // false, we skip the merge entirely even if `carouselAds` is
    // non-empty (defense-in-depth; the server also returns `[]` in that
    // case).
    const merged =
      config.carouselEnabled && carouselAds.length > 0
        ? (mergeAdsIntoCards({
            cards: orderedCardsRef.current,
            ads: carouselAds,
            renderAd: buildAdRenderer,
            interval: config.carouselInterval,
            minBefore: config.carouselMinBefore,
          }) as Card[])
        : orderedCardsRef.current;

    return {
      orderedCards: addCard
        ? [
            ...(addCard?.insertAt === "first"
              ? [
                  {
                    ...DEFAULT_CARD,
                    id: "insert-first-card",
                    renderComponent: addCard.component,
                  } as Card,
                ]
              : []),
            ...merged,
            ...(addCard?.insertAt === "last"
              ? [
                  {
                    ...DEFAULT_CARD,
                    id: "insert-last-card",
                    renderComponent: addCard.component,
                  } as Card,
                ]
              : []),
          ]
        : merged,
    };
  }, [orderedCards, carouselAds, config]);
};
